import { useState } from 'react'
import { Loader2, Save, X } from 'lucide-react'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { MOTIVOS_DESCARTE } from '@/features/rh/lib/reclutamientoTablero'
import type { Candidate } from '@/services/api'

interface ModalDeDescarteDeCandidatoProps {
  candidato: Candidate
  guardando: boolean
  onCancelar: () => void
  onGuardar: (motivo: string, comentario: string) => void
}

/**
 * Descartar un candidato: motivo del catálogo y comentario libre.
 *
 * Es el mismo para el tablero y para la ficha. Descartar pide el motivo antes
 * porque es lo que después se ve en la tabla de descartados; sin preguntarlo,
 * esa columna llegaría vacía. Se monta solo mientras hay un descarte en curso,
 * así que el motivo y el comentario empiezan vacíos cada vez.
 */
export const ModalDeDescarteDeCandidato = ({
  candidato,
  guardando,
  onCancelar,
  onGuardar,
}: ModalDeDescarteDeCandidatoProps) => {
  const [motivo, setMotivo] = useState('')
  const [comentario, setComentario] = useState('')

  return (
    <CapaDeModal zIndex={60}>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <button
          onClick={onCancelar}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-xl font-bold text-gray-900 pr-8">Descartar candidato</h2>
        <p className="text-sm text-gray-500 mt-1">
          {candidato.fullName}
          {candidato.jobRequest?.puestoSolicitar
            ? ` · ${candidato.jobRequest.puestoSolicitar}`
            : ''}
        </p>

        <label className="block text-sm font-medium text-gray-700 mt-5 mb-1">
          Motivo <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <SelectBuscable
            variante="formulario"
            value={motivo}
            onChange={setMotivo}
            placeholder="Seleccionar el motivo de descarte"
            placeholderDeBusqueda="Buscar motivo..."
            opciones={MOTIVOS_DESCARTE.map((texto) => ({ value: texto, label: texto }))}
          />
        </div>

        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Comentario</label>
        <textarea
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          rows={3}
          placeholder="Agrega un comentario..."
          className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA] resize-none"
        />

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancelar} disabled={guardando} className={estiloDeBoton('secundario')}>
            Cancelar
          </button>
          <button
            onClick={() => onGuardar(motivo, comentario)}
            disabled={!motivo || guardando}
            className={estiloDeBoton('primario')}
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}
